// src/context/UserContext.jsx
import { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import toast from "../utils/toast";
import authService from "../services/authService";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const logout = async () => {
    setUser(null);
    await authService.logout();
  };

  const fetchUser = async () => {
    try {
      // Add a small delay to allow cookies to be set after login
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const data = await authService.getCurrentUser();
      
      if (!data) {
        setUser(null);
        return;
      }

      // Handle status-based errors (pending, denied, suspended)
      if (data.status && ['pending', 'denied', 'suspended'].includes(data.status)) {
        console.warn('User account status issue:', data.status, data.message);
        setUser(null);
        toast.error(data.message || 'Account access restricted');
        return;
      }

      setUser(data);
      // Store only safe user data in localStorage
      const safeUserData = authService.getSafeUserData(data);
      if (safeUserData) {
        localStorage.setItem('user', JSON.stringify(safeUserData));
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setUser(null);
      localStorage.removeItem('user');
    } finally {
      setLoadingUser(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <UserContext.Provider
      value={{ user, loadingUser, setUser, logout, fetchUser }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);